/**
 * ZEGAR I STREFA AGENTA — co pokazać człowiekowi i kiedy krzyczeć.
 *
 * ⚠️ WYDZIELONE Z EKRANU, ŻEBY DAŁO SIĘ TO SPRAWDZIĆ TESTEM. Reguła ma trzy przypadki brzegowe,
 * z których każdy da się pomylić po cichu: brak pomiaru udający zgodność, strefa poprawna w innym
 * kraju i strefa niezgodna z wyspą przy zerowym rozjeździe. Wpięte wprost w JSX nie sprawdziłby
 * ich nikt.
 */

/** Od ilu sekund rozjazd jest wart uwagi. Ta sama granica co u agenta i z tego samego powodu. */
export const PROG_ROZJAZDU_S = 300;

export type Waga = 'ok' | 'uwaga' | 'nieznane';

export type OpisZegara = { tekst: string; waga: Waga };

/**
 * Rozjazd zegara jako zdanie.
 *
 * ⚠️ `null`/`undefined` DAJE „nie zmierzono", NIGDY „0 s". Zero jest twierdzeniem, że zegar się
 * zgadza — a przy braku pomiaru nikt tego nie sprawdził. To ta sama zasada, którą stosujemy
 * po stronie portalu przy `probe.reachability = NIEZNANY`.
 */
export function opisRozjazdu(sekundy: number | null | undefined): OpisZegara {
  if (sekundy === null || sekundy === undefined) return { tekst: 'nie zmierzono', waga: 'nieznane' };

  const abs = Math.abs(sekundy);
  if (abs < 60) return { tekst: `${sekundy > 0 ? '+' : sekundy < 0 ? '−' : ''}${abs} s`, waga: abs > PROG_ROZJAZDU_S ? 'uwaga' : 'ok' };

  const minuty = Math.round(abs / 60);
  const znak = sekundy > 0 ? '+' : '−';
  /** Godziny dopiero od 90 minut — „90 min" czyta się lepiej niż „2 h" przy 1 h 30. */
  const tekst = minuty >= 90 ? `${znak}${(abs / 3600).toFixed(1)} h` : `${znak}${minuty} min`;
  return { tekst, waga: abs > PROG_ROZJAZDU_S ? 'uwaga' : 'ok' };
}

export type OpisStrefy = { tekst: string; waga: Waga; powod: string | null };

/**
 * Strefa maszyny w zestawieniu z OCZEKIWANĄ dla wyspy.
 *
 * ⚠️ SAMA STREFA NIC NIE ZNACZY. `Europe/London` jest poprawne w Londynie i błędne w Pile —
 * wartość ma dopiero porównanie. Dlatego funkcja bierze obie i nie da się jej użyć z jedną.
 *
 * ⚠️ ROZJAZD ZEGARA TEGO NIE WYKRYJE i to jest cały powód, dla którego ten wskaźnik istnieje.
 * Windows trzyma UTC osobno od strefy, więc maszyna ze złą strefą i dobrym czasem ma rozjazd
 * ZERO — a SPS zapisuje wydania czasem lokalnym, czyli wpisuje do bazy przesunięte godziny.
 */
export function opisStrefy(strefaWezla: string | null | undefined, strefaWyspy: string | null | undefined): OpisStrefy {
  if (strefaWezla === null || strefaWezla === undefined || strefaWezla === '') {
    return { tekst: 'nie podano', waga: 'nieznane', powod: null };
  }
  if (strefaWyspy === null || strefaWyspy === undefined || strefaWyspy === '') {
    /** Pierwszy agent wyspy — nie ma z czym porównać, ale sama wartość jest już informacją. */
    return { tekst: strefaWezla, waga: 'ok', powod: null };
  }
  if (strefaWezla !== strefaWyspy) {
    return {
      tekst: strefaWezla,
      waga: 'uwaga',
      powod: `wyspa oczekuje ${strefaWyspy} — SPS zapisuje wydania czasem lokalnym, więc godziny z tej maszyny mogą być przesunięte`,
    };
  }
  return { tekst: strefaWezla, waga: 'ok', powod: null };
}

/**
 * Czy strefa odbiega od polskiej — do WYRÓŻNIENIA, nie do ostrzeżenia.
 *
 * Większość maszyn stoi w Polsce, więc `Europe/Warsaw` jest tłem i nie musi rzucać się w oczy.
 * Holandia, Niemcy czy Węgry mają się wyróżniać — ale jako fakt, nie jako usterka: tam ta strefa
 * jest poprawna.
 */
export function strefaNietypowa(strefa: string | null | undefined): boolean {
  return strefa !== null && strefa !== undefined && strefa !== '' && strefa !== 'Europe/Warsaw';
}

export type TypStartu = 'automatyczny' | 'reczny' | 'wylaczona' | 'nieznany';

/**
 * SYNCHRONIZACJA CZASU NA MASZYNIE — czy rozjazd zniknie sam.
 *
 * ⚠️ TO JEST PYTANIE, NA KTÓRE SAM ROZJAZD NIE ODPOWIADA, i dlatego ten wskaźnik istnieje.
 * Maszyna z rozjazdem 900 s i działającą usługą czasu wyrówna się przy najbliższej synchronizacji;
 * ta sama maszyna z usługą WYŁĄCZONĄ nie wyrówna się nigdy i będzie odjeżdżać dalej. Z ekranu
 * wyglądały dotąd identycznie, a wymagają zupełnie różnych reakcji: jednej się nie rusza,
 * do drugiej trzeba pojechać albo włączyć usługę zdalnie.
 */
export function opisSynchronizacji(
  typStartu: TypStartu | null | undefined,
  dziala: boolean | null | undefined,
): OpisZegara & { powod: string | null } {
  if (typStartu === null || typStartu === undefined || typStartu === 'nieznany') {
    return { tekst: 'nie ustalono', waga: 'nieznane', powod: null };
  }
  if (typStartu === 'wylaczona') {
    return {
      tekst: 'WYŁĄCZONA',
      waga: 'uwaga',
      powod:
        'Usługa czasu Windows jest wyłączona — ta maszyna NIE wyrówna zegara sama, niezależnie od tego, ' +
        'ile się poczeka. Trzeba ją włączyć.',
    };
  }
  if (dziala === false) {
    return {
      tekst: `${typStartu}, zatrzymana`,
      waga: 'uwaga',
      powod: 'Usługa czasu jest zainstalowana, ale nie działa — synchronizacja nie nastąpi, dopóki jej ktoś nie uruchomi.',
    };
  }
  /**
   * ⚠️ „Działa" nie znaczy „czas jest dobry". NTP bywa zablokowany, a źródło nieskonfigurowane —
   * dlatego przy działającej usłudze i dużym rozjeździe nadal świeci ostrzeżenie o rozjeździe.
   * Mówimy tylko tyle, czego jesteśmy pewni.
   */
  return { tekst: typStartu === 'automatyczny' ? 'automatyczna' : 'ręczna, działa', waga: 'ok', powod: null };
}
